/**
 * PurchaseCosts — the "Buying Costs" section (Figma CMS node `6949:87166`),
 * rendered after Features in each property.
 *
 * What's dynamic vs static:
 *   - The property price and the 9–11% estimated-cost range are **per-offer**
 *     (computed from the price in mapProperty.js / mockData.js → `offer.purchaseCosts`).
 *   - The tax breakdown row is driven by the offer's scenario (resale ITP /
 *     new-build or plot IVA + AJD) via `offer.purchaseCosts`; the remaining rows,
 *     footnotes and "Additional Information" are standard buyer-cost guidance built
 *     from localized strings here. All figures are ESTIMATES pending legal review.
 *
 * The header row shows the "Buying Costs" heading beside the estimated-cost
 * summary (label / range / toggle). Everything below it — the property price,
 * the breakdown table, the notes and the additional info — is collapsed by
 * default; the "Show breakdown" toggle in the header expands the whole body.
 *
 * Props:
 *   - offer {{ priceLabel, purchaseCosts }} — `purchaseCosts` may be null (no price)
 */
import React, { useState } from 'react';
import { useStrings } from './i18n';
import { IconChevronDown } from './icons';
import Money from './Money';

// Breakdown rows, driven by the offer's tax scenario (resale ITP / new-build or
// plot IVA), so only the applicable tax shows. Percent values stay literal (not
// translated); the worded concepts/applications come from `t`. ESTIMATE ONLY.
const costRows = (t, pc) => {
    const rows = [];
    if (pc.scenario === 'resale') {
        // Resale dwelling → regional ITP (no AJD).
        rows.push({ key: 'transfer', concept: t.rowTransferTax, application: `${pc.taxPct}%` });
    } else if (pc.scenario === 'plot') {
        // Plot / land → IVA at the 21% general rate. Plain VAT label (no "new
        // build" — a plot is sold commercially or personally, not built).
        rows.push({ key: 'vat', concept: t.rowVatLand, application: `${pc.taxPct}%` });
        rows.push({ key: 'ajd', concept: t.rowStampDuty, application: `~${pc.ajdPct != null ? pc.ajdPct : 1.5}%` });
    } else {
        // New build → IVA 10% (4% protected) + AJD.
        rows.push({ key: 'vat', concept: t.rowVat, application: t.vatApplication });
        rows.push({ key: 'ajd', concept: t.rowStampDuty, application: `~${pc.ajdPct != null ? pc.ajdPct : 1.5}%` });
    }
    rows.push({ key: 'notary', concept: t.rowNotary, application: t.officialTariff });
    rows.push({ key: 'registry', concept: t.rowLandRegistry, application: t.officialTariff });
    return rows;
};

export default function PurchaseCosts({ offer }) {
    const t = useStrings();
    const [open, setOpen] = useState(false); // breakdown collapsed by default
    const pc = offer.purchaseCosts;
    if (!pc) return null;        // no price → no estimate to show
    if (offer.isRental) return null; // buying costs don't apply to rentals
    return (
        <section className="rs-section rs-buy">
            <div className="rs-buy__head">
                <h2 className="rs-section__h">{t.buyingCosts}</h2>
                <div className="rs-buy__est">
                    <span className="rs-buy__k">{t.estimatedPurchaseCosts}</span><br />
                    <span className="rs-buy__range">
                        <Money>{`${pc.lowPct}% – ${pc.highPct}% (${pc.lowLabel} – ${pc.highLabel})`}</Money>
                    </span><br />
                    <button type="button" className={`rs-buy__toggle ${open ? 'is-open' : ''}`}
                        onClick={() => setOpen((v) => !v)} aria-expanded={open}>
                        {open ? t.hide : t.showBreakdown} <IconChevronDown size={16} />
                    </button>
                </div>
            </div>

            {open && (
                <div className="rs-buy__body">
                    <div className="rs-buy__price">
                        <div><span className="rs-buy__k">{t.propertyPrice}</span><br /><span className="rs-buy__v"><Money>{offer.priceLabel}</Money></span></div>
                        <p className="rs-buy__note">{t.priceNote}</p>
                    </div>

                    <div className="rs-buy__card">
                        <div className="rs-buy__title">{t.purchaseCostBreakdown}</div>
                        <div className="rs-buy__table">
                            <div className="rs-buy__row rs-buy__row--head">
                                <div className="rs-buy__cell">{t.concept}</div>
                                <div className="rs-buy__cell">{t.application}</div>
                            </div>
                            {costRows(t, pc).map((r) => (
                                <div className="rs-buy__row" key={r.key}>
                                    <div className="rs-buy__cell">{r.concept}</div>
                                    <div className="rs-buy__cell">{r.application}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rs-buy__foot">
                        <p>{t.costRangeNotePrefix} ({pc.lowPct}% {t.and} {pc.highPct}%) {t.ofPurchasePrice}</p>
                        <p>{t.costApproxNote}</p>
                    </div>
                    <div className="rs-buy__info">
                        <p className="rs-buy__info-h">{t.additionalInformation}</p>
                        {/* The Abbreviated Information Document is an Andalusian
                            requirement (Decree 218/2005); other regions (e.g.
                            Valencia) have no equivalent, so only show it there. */}
                        {pc.community === 'andalucia' && <p>{t.addInfoDocument}</p>}
                        <p>{t.addInfoNoFees}</p>
                    </div>
                </div>
            )}
        </section>
    );
}
